import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { fetchParties, fetchOverview, api } from "@/lib/api";
import Seo from "@/components/Seo";
import { X, Plus, GitCompare } from "lucide-react";
import { TrustBar } from "@/components/PartyCard";

const MAX_SELECT = 3;

export default function Compare() {
    const [parties, setParties] = useState([]);
    const [overview, setOverview] = useState([]);
    const [selected, setSelected] = useState([]);
    const [fullParties, setFullParties] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();

    // Load initial + parse URL — kun én gang ved mount.
    // searchParams læses bevidst kun her; selve "selected" persistes via ?partier=
    // i den følgende effect.
    useEffect(() => {
        Promise.all([fetchParties(), fetchOverview()]).then(([p, o]) => {
            setParties(p);
            setOverview(o);
            const ids = (searchParams.get("partier") || "").split(",").filter(Boolean);
            if (ids.length) {
                setSelected(ids.filter((id) => p.some((x) => x.id === id)).slice(0, MAX_SELECT));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load full details for selected parties + sync URL
    useEffect(() => {
        async function load() {
            const results = {};
            for (const id of selected) {
                if (!fullParties[id]) {
                    results[id] = await api.get(`/parties/${id}`).then((r) => r.data);
                }
            }
            if (Object.keys(results).length) {
                setFullParties((f) => ({ ...f, ...results }));
            }
        }
        if (selected.length) load();
        setSearchParams(selected.length ? { partier: selected.join(",") } : {}, { replace: true });
        // fullParties bevidst udeladt — vi tjekker eksistens inde i effekten,
        // og vil ikke trigge re-run hver gang vi sætter en ny party-detail.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const statsById = useMemo(
        () => Object.fromEntries(overview.map((o) => [o.party_id, o])),
        [overview]
    );

    const selectedFull = selected.map((id) => fullParties[id]).filter(Boolean);
    const availableToAdd = parties.filter((p) => !selected.includes(p.id));
    const canAdd = selected.length < MAX_SELECT;

    const addParty = (id) => {
        if (selected.includes(id) || selected.length >= MAX_SELECT) return;
        setSelected([...selected, id]);
    };

    const removeParty = (id) => {
        setSelected(selected.filter((x) => x !== id));
    };

    // Saml alle emner fra stances
    const allTopics = useMemo(() => {
        const set = new Set();
        selectedFull.forEach((p) => {
            Object.keys(p.stances || {}).forEach((t) => set.add(t));
        });
        return Array.from(set);
    }, [selectedFull]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="compare-page">
            <Seo
                title="Sammenlign danske partier"
                description="Sammenlign op til 4 danske partier side om side på tillidsscore, valgløfter, løftebrud og politiske holdninger. Få et direkte overblik før dit næste valg."
                keywords="sammenlign partier, partisammenligning, politiske holdninger, tillidsscore, danske partier, stemmeguide"
                path="/sammenlign"
            />
            <div className="mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-royal-soft text-royal text-xs uppercase tracking-[0.18em] font-semibold mb-4">
                    <GitCompare className="w-3.5 h-3.5" />
                    Sammenlign partier
                </div>
                <h1 className="font-serif-dm text-5xl text-royal leading-tight">
                    Partierne side om side
                </h1>
                <p className="text-[#4F5A7A] mt-3 max-w-2xl">
                    Vælg op til {MAX_SELECT} partier og sammenlign dem direkte — tillid,
                    holdninger, mærkesager og nøgletal i ét overblik.
                </p>
            </div>

            {/* Vælg partier */}
            <div className="bg-white border border-[#E5E8F0] rounded-2xl p-5 md:p-6 mb-8">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    {selected.length === 0 && (
                        <p className="text-sm text-[#4F5A7A]">Vælg partier nedenfor for at starte sammenligningen.</p>
                    )}
                    {selectedFull.map((p) => (
                        <span
                            key={p.id}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-semibold"
                            style={{ backgroundColor: p.color }}
                            data-testid={`selected-${p.id}`}
                        >
                            {p.name}
                            <button
                                onClick={() => removeParty(p.id)}
                                className="hover:bg-white/20 rounded-full p-0.5"
                                aria-label="Fjern"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    ))}
                </div>

                {canAdd && (
                    <div className="flex flex-wrap gap-2">
                        {availableToAdd.map((p) => (
                            <button
                                key={p.id}
                                onClick={() => addParty(p.id)}
                                data-testid={`add-party-${p.id}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E5E8F0] bg-white text-sm hover:bg-royal-soft transition-colors"
                            >
                                <span
                                    className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
                                    style={{ backgroundColor: p.color }}
                                >
                                    {p.short_name}
                                </span>
                                <span>{p.name}</span>
                                <Plus className="w-3.5 h-3.5 text-[#4F5A7A]" />
                            </button>
                        ))}
                    </div>
                )}
                {!canAdd && (
                    <p className="text-xs text-[#4F5A7A] italic">Maks {MAX_SELECT} partier ad gangen.</p>
                )}
            </div>

            {selected.length > 0 && selectedFull.length === selected.length && (
                <div className="space-y-6">
                    {/* Nøgletal */}
                    <CompareGrid title="Tillid & tal" columns={selectedFull}>
                        {selectedFull.map((p) => {
                            const s = statsById[p.id];
                            return (
                                <div key={p.id} className="p-5 md:p-6 border-l border-[#EEF0F7] first:border-l-0">
                                    {s && <TrustBar score={s.trust_score} />}
                                    {s && (
                                        <div className="grid grid-cols-2 gap-3 mt-5 pt-5 border-t border-[#EEF0F7]">
                                            <Stat label="Holdt" value={s.holdt} color="#186A3B" />
                                            <Stat label="Brudt" value={s.brudt} color="#BB1B2D" />
                                            <Stat label="Løgne" value={s.lies_count} color="#BB1B2D" />
                                            <Stat label="Valgflæsk" value={s.valgflaesk_count} color="#C78300" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </CompareGrid>

                    {/* Basisinfo */}
                    <CompareGrid title="Basisinformation" columns={selectedFull}>
                        {selectedFull.map((p) => (
                            <div key={p.id} className="p-5 md:p-6 text-sm space-y-2 border-l border-[#EEF0F7] first:border-l-0">
                                <InfoRow label="Leder" value={p.leader} />
                                <InfoRow label="Ideologi" value={p.ideology} />
                                <InfoRow label="Stiftet" value={p.founded} />
                                <InfoRow label="Mandater" value={p.seats} />
                                {p.member_count && <InfoRow label="Medlemmer" value={`ca. ${p.member_count.toLocaleString("da-DK")}`} />}
                                {p.website && (
                                    <InfoRow label="Website" value={
                                        <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-royal hover:text-danish-red underline">
                                            {p.website.replace("https://", "")}
                                        </a>
                                    } />
                                )}
                            </div>
                        ))}
                    </CompareGrid>

                    {/* Mærkesager */}
                    <CompareGrid title="Mærkesager" columns={selectedFull}>
                        {selectedFull.map((p) => (
                            <div key={p.id} className="p-5 md:p-6 border-l border-[#EEF0F7] first:border-l-0">
                                <ul className="space-y-2 text-sm">
                                    {(p.key_policies || []).map((policy, i) => (
                                        <li key={`${p.id}-policy-${policy.slice(0, 40)}`} className="flex items-start gap-2">
                                            <span
                                                className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                                                style={{ backgroundColor: p.color }}
                                            >
                                                {i + 1}
                                            </span>
                                            <span className="text-[#0B1A3A]">{policy}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </CompareGrid>

                    {/* Holdninger pr. emne */}
                    {allTopics.length > 0 && (
                        <CompareGrid title="Holdninger" columns={selectedFull}>
                            {selectedFull.map((p) => (
                                <div key={p.id} className="p-5 md:p-6 border-l border-[#EEF0F7] first:border-l-0 space-y-4">
                                    {allTopics.map((topic) => (
                                        <div key={topic}>
                                            <div
                                                className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold text-white mb-1"
                                                style={{ backgroundColor: p.color }}
                                            >
                                                {topic}
                                            </div>
                                            <p className="text-sm text-[#0B1A3A] leading-snug">
                                                {p.stances?.[topic] || <span className="text-[#8A94AE] italic">Ingen data</span>}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </CompareGrid>
                    )}

                    {/* CTA */}
                    <div className="grid" style={{ gridTemplateColumns: `repeat(${selectedFull.length}, 1fr)` }}>
                        {selectedFull.map((p) => (
                            <Link
                                key={p.id}
                                to={`/partier/${p.id}`}
                                className="mx-2 px-4 py-3 rounded-full text-center text-white font-semibold text-sm hover:brightness-110 transition-all"
                                style={{ backgroundColor: p.color }}
                                data-testid={`view-party-${p.id}`}
                            >
                                Se {p.name} →
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function CompareGrid({ title, columns, children }) {
    return (
        <div className="bg-white border border-[#E5E8F0] rounded-2xl overflow-hidden">
            <div className="px-5 md:px-6 py-4 border-b border-[#EEF0F7]">
                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">{title}</span>
            </div>
            <div
                className="grid"
                style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
            >
                <div className="contents">
                    {/* Partinavn-header pr. kolonne */}
                    {columns.map((p) => (
                        <div key={`h-${p.id}`} className="px-5 md:px-6 py-4 border-l border-[#EEF0F7] first:border-l-0 flex items-center gap-2.5">
                            <span
                                className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                                style={{ backgroundColor: p.color }}
                            >
                                {p.short_name}
                            </span>
                            <span className="font-serif-dm text-lg text-royal truncate">{p.name}</span>
                        </div>
                    ))}
                </div>
                {children}
            </div>
        </div>
    );
}

function Stat({ label, value, color }) {
    return (
        <div>
            <div className="font-serif-dm text-2xl" style={{ color }}>{value}</div>
            <div className="text-[11px] text-[#4F5A7A]">{label}</div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="flex justify-between gap-3">
            <span className="text-[#4F5A7A] text-xs uppercase tracking-wider font-semibold">{label}</span>
            <span className="text-[#0B1A3A] text-right">{value}</span>
        </div>
    );
}
